import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FileText, Eye, EyeOff, CheckCircle } from "lucide-react";

export default function Register() {
  const { register, formatError, user } = useAuth();
  const navigate = useNavigate();
  // Already signed in → bounce to Dashboard instead of showing the form again.
  useEffect(() => {
    if (user) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);
  const [form, setForm] = useState({ email: "", password: "", name: "", center_name: "" });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const result = await register(form.email, form.password, form.name, form.center_name);
      // Sprint 2 Batch 3 — registration requires email verification
      if (result?.needs_verification) {
        navigate(`/verify-email?email=${encodeURIComponent(form.email)}`);
      } else {
        navigate("/app/dashboard");
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12"
        style={{ background: "linear-gradient(180deg, #6D28D9 0%, #2563EB 100%)" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-bold text-sm" style={{ fontFamily: "Manrope" }}>Simple Sign In Tax</p>
            <p className="text-white/60 text-xs">for Childcare Centers</p>
          </div>
        </div>
        <div>
          <h2 className="text-4xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: "Manrope" }}>
            Start your free<br />14-day trial today
          </h2>
          {["14 days completely free", "No credit card required", "Upload unlimited documents", "Generate TurboTax-ready reports", "Plans start at $99/year after trial"].map(item => (
            <div key={item} className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
              <span className="text-white/80 text-sm">{item}</span>
            </div>
          ))}
        </div>
        <p className="text-white/40 text-sm">© 2026 Simple Sign In Tax</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #6D28D9, #2563EB)" }}>
              <FileText className="w-4 h-4 text-white" />
            </div>
            <p className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>Simple Sign In Tax</p>
          </div>

          <h1 className="text-3xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "Manrope" }}>Create your account</h1>
          <p className="text-slate-500 mb-8">Start your free 14-day trial — no credit card needed</p>

          {error && (
            <div data-testid="register-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Your Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Smith"
                required
                data-testid="register-name-input"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Childcare Center Name</label>
              <input
                type="text"
                value={form.center_name}
                onChange={e => setForm({ ...form, center_name: e.target.value })}
                placeholder="Little Stars Learning Center"
                required
                data-testid="register-center-name-input"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="you@yourcenter.com"
                required
                data-testid="register-email-input"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="At least 6 characters"
                  required
                  data-testid="register-password-input"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] bg-white pr-11"
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg transition-all mt-2"
            >
              {loading ? "Creating account..." : "Start Free 14-Day Trial"}
            </button>
          </form>

          <p className="text-center text-slate-500 text-sm mt-4">
            Already have an account?{" "}
            <Link to="/login" className="text-[#2563EB] font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
          </p>
          <p className="text-center text-slate-400 text-xs mt-3">
            Trial is free for 14 days. Then choose a plan — starting at $99/year.
          </p>
          <p className="text-center text-slate-400 text-[11px] mt-4 leading-relaxed" data-testid="register-legal-line">
            By creating an account you agree to our{" "}
            <Link to="/terms" target="_blank" className="text-slate-500 hover:text-[#2563EB] underline">Terms of Service</Link>{" "}
            and{" "}
            <Link to="/privacy" target="_blank" className="text-slate-500 hover:text-[#2563EB] underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
